import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { BiddingBidMarket } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { listJepxSettlements } from '@ebalance/proto/bffuser/v1/jepx_settlement_service-JepxSettlementService_connectquery'
import {
  ListJepxSettlementsRequestSchema,
  type ListJepxSettlementsResponse,
} from '@ebalance/proto/bffuser/v1/jepx_settlement_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'

export type ListJepxSettlementsParams = {
  market: BiddingBidMarket
  /** 清算日の期間の開始日 (含む)。 未指定 = 下限なし。 */
  settlementDateFrom: CivilDate | undefined
  /** 清算日の期間の終了日 (含む)。 未指定 = 上限なし。 */
  settlementDateTo: CivilDate | undefined
}

/**
 * BFF `JepxSettlementService.ListJepxSettlements` を取得する hook
 * (`docs/specs/bidding/jepx-settlement.md` §1)。
 *
 * - 期間はサーバー側で絞る (request の `settlement_date_from` / `settlement_date_to`、 各々任意)。
 *   BFF は全件を返し、 ページ送りの引数は無い。
 * - **全画面エラー化を避けるため `throwOnError: false` を明示する** (AC-JST-005 は画面内の案内)。
 *   global QueryClient の既定は `throwOnError: true` で、 何も指定しないと取得失敗が
 *   router の errorElement (= 全画面エラー) へ飛ぶ。 retry は `ApiClientProvider` の
 *   `shouldRetryQuery` / `queryRetryDelay` に委譲する (一過性エラーだけ有限回リトライし、
 *   リトライ中は `isPending` のまま = 一覧は「読み込み中」)。
 *   前例: `pages/SpotBidding/hooks/useSpotFeeYenPerKwh.ts`、
 *   `pages/ProcurementPlanList/hooks/useListProcurementPlans.ts`。
 */
export function useListJepxSettlements({
  market,
  settlementDateFrom,
  settlementDateTo,
}: ListJepxSettlementsParams): UseQueryResult<
  ListJepxSettlementsResponse,
  ConnectError
> {
  const request = create(ListJepxSettlementsRequestSchema, {
    market,
    settlementDateFrom:
      settlementDateFrom !== undefined
        ? civilDateToProtoDate(settlementDateFrom)
        : undefined,
    settlementDateTo:
      settlementDateTo !== undefined
        ? civilDateToProtoDate(settlementDateTo)
        : undefined,
  })

  return useQuery(listJepxSettlements, request, { throwOnError: false })
}
